import { useMemo, useState, useEffect } from 'react';
import {
  Grid, TextField, MenuItem, Switch, FormControlLabel, Typography, Alert,
  InputAdornment, IconButton, Stack, Tooltip,
} from '@mui/material';
import { AutoFixHigh } from '@mui/icons-material';
import FormDrawer from '../ui/FormDrawer';
import { FormSection, FormGrid } from '../ui/forms';
import FormFileUpload from '../ui/forms/FormFileUpload';
import PersonNameField from '../ui/forms/PersonNameField';
import SearchableMasterSelect from './SearchableMasterSelect';
import SearchableMasterMultiSelect from './SearchableMasterMultiSelect';
import ParentMasterSelect from './ParentMasterSelect';
import RestaurantSlugSelect from './RestaurantSlugSelect';
import ZoneMasterSelect from './ZoneMasterSelect';
import HubCoordinatePicker, { isTransportHubType } from './HubCoordinatePicker';
import { GEO_FIELD_SOURCE } from '../../constants/masterSetupFlow';
import { getMasterTypeForField, LEGACY_PARENT_META_KEY } from '../../constants/masterRefFields';
import { ON_DEMAND_AUTO_CODE_TYPES, slugifyMasterCode } from '../../constants/onDemandMasters';
import { TRANSPORT_AUTO_CODE_TYPES, TRANSPORT_LETTER_ONLY_NAME_TYPES, TRANSPORT_HIDE_STATUS_TOGGLE_TYPES, TRANSPORT_HIDE_DESCRIPTION_TYPES, TRANSPORT_SEQUENTIAL_SORT_ORDER_TYPES, TRANSPORT_INTEGER_SORT_ORDER_TYPES, TRANSPORT_UNIQUE_SORT_ORDER_TYPES, TRANSPORT_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES, TRANSPORT_DESCRIPTION_VALIDATED_TYPES, filterRecordsForParentScopedSortOrder, PASSENGER_CAPACITY_MIN, PASSENGER_CAPACITY_MAX, isVehiclePassengerCapacityField, sanitizePassengerCapacityInput, validatePassengerCapacity, validateVehicleServiceTypes, isRideTagColorField, sanitizeRideTagColorInput, validateRideTagColor, parseCommaSeparatedDayValues, formatCommaSeparatedDayValues } from '../../constants/transportSubserviceMasters';
import { isAssetUrlField } from '../../utils/assetUrl';
import { chargeFieldLabel } from '../../utils/currency';
import { FOOD_IMAGE_SPECS } from '../../constants/foodMasterImageSpecs';
import { validateMasterForm, isMasterFormValid, type FieldErrors, validateDuplicateMasterCode, validateDuplicateSortOrder } from '../../utils/masterFormValidation';
import {
  validateOptionalDescription,
  limitConsecutiveRepeatedCharacters,
} from '../../utils/descriptionValidation';
import type { MasterSchema, MasterFieldDef } from '../../constants/masters';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';

const MASTER_CODE_PLACEHOLDERS: Record<string, string> = {
  trip_types: 'LOCAL',
  vehicle_types: 'SEDAN',
  ride_attributes: 'AC',
  ride_attribute_values: 'AC_YES',
  ride_tags: 'FEATURED',
  service_availability_schedules: 'WEEKDAY_MORNING',
  service_categories: 'ELECTRICIAN',
  skills: 'WIRING',
  certificates: 'ELECTRICIAN_LICENSE',
  experience_levels: 'SENIOR',
  pricing_templates: 'FIXED_VISIT',
  visit_charges: 'STANDARD_VISIT',
  consultation_types: 'MEDICAL',
  organizer_types: 'VENUE',
};

/** Types that use primary→sub self-hierarchy (parent optional for primary). */
const OPTIONAL_PARENT_TYPES = new Set(['service_categories', 'food_categories', 'categories']);

function masterCodePlaceholder(type: string): string {
  return MASTER_CODE_PLACEHOLDERS[type] || 'UNIQUE_CODE';
}

function AutoCodeAdornment({
  auto,
  disabled,
  onAutoChange,
  onRegenerate,
}: {
  auto: boolean;
  disabled?: boolean;
  onAutoChange: (on: boolean) => void;
  onRegenerate: () => void;
}) {
  return (
    <InputAdornment position="end">
      <Stack direction="row" alignItems="center" spacing={0.25}>
        <Tooltip title="Auto-fill code from name">
          <span>
            <IconButton
              size="small"
              edge="end"
              aria-label="Auto-fill code from name"
              disabled={disabled}
              onClick={onRegenerate}
            >
              <AutoFixHigh fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <FormControlLabel
          sx={{ mr: 0, ml: 0.25 }}
          control={(
            <Switch
              size="small"
              checked={auto}
              onChange={(e) => onAutoChange(e.target.checked)}
            />
          )}
          label="Auto"
        />
      </Stack>
    </InputAdornment>
  );
}

export interface MasterFormState {
  code: string;
  name: string;
  description: string;
  country: string;
  state: string;
  city: string;
  district: string;
  parentId: string;
  sortOrder: number;
  isActive: boolean;
  metadata: Record<string, string | number | boolean>;
}

interface ParentOption {
  _id: string;
  name: string;
  code: string;
}

interface Props {
  open: boolean;
  editing: boolean;
  schema?: MasterSchema | null;
  currentType: string;
  form: MasterFormState;
  onChange: (form: MasterFormState) => void;
  onClose: () => void;
  onSubmit: () => void;
  loading?: boolean;
  parentMasterType?: string;
  parentOptions?: ParentOption[];
  /** When set on create, sort order is locked to this sequential value. */
  lockedSortOrder?: number | null;
  /** Existing records for duplicate code validation on create/edit. */
  existingRecords?: Array<{ _id?: string; code?: string; sortOrder?: number; parentId?: string; attributeCode?: string }>;
  editingId?: string;
  saveError?: string | null;
}

function isZoneField(field: MasterFieldDef) {
  return field.type === 'zone' || field.key === 'zoneCode';
}

function isMasterField(field: MasterFieldDef, masterTypeKey: string) {
  return field.type === 'master' || !!getMasterTypeForField(field, masterTypeKey);
}

function isEntityField(field: MasterFieldDef) {
  return field.type === 'entity' || field.entitySource === 'restaurants';
}

function imageConstraintsForField(field: MasterFieldDef, masterTypeKey: string, metadata: Record<string, string | number | boolean>) {
  if (field.imageMinWidth || field.imageMinHeight) {
    return {
      minWidth: field.imageMinWidth,
      minHeight: field.imageMinHeight,
      maxWidth: field.imageMaxWidth,
      maxHeight: field.imageMaxHeight,
      aspectRatio: field.imageAspectRatio,
    };
  }
  if (masterTypeKey === 'food_images' && field.key === 'imageUrl') {
    const imageType = String(metadata.imageType || 'thumbnail');
    const spec = imageType === 'gallery' ? FOOD_IMAGE_SPECS.gallery : FOOD_IMAGE_SPECS.thumbnail;
    return {
      minWidth: spec.minWidth,
      minHeight: spec.minHeight,
      maxWidth: spec.maxWidth,
      maxHeight: spec.maxHeight,
      aspectRatio: spec.aspectRatio,
    };
  }
  return undefined;
}

function MetadataFields({ fields, values, onChange, masterTypeKey, errors, showErrors, liveMetaKeys = [] }: {
  fields: MasterFieldDef[];
  values: Record<string, string | number | boolean>;
  onChange: (key: string, value: string | number | boolean) => void;
  masterTypeKey: string;
  errors: FieldErrors;
  showErrors: boolean;
  liveMetaKeys?: string[];
}) {
  const { t } = useI18n();
  if (!fields.length) return null;

  const labelOf = (text?: string) => (text ? translateUiText(text, t) : text);

  const sections = useMemo(() => {
    const map = new Map<string, MasterFieldDef[]>();
    fields.forEach((f) => {
      const sec = f.section || 'Details';
      if (!map.has(sec)) map.set(sec, []);
      map.get(sec)!.push(f);
    });
    return Array.from(map.entries());
  }, [fields]);

  const renderField = (field: MasterFieldDef) => {
    const errKey = `meta:${field.key}`;
    const fieldError = (showErrors || liveMetaKeys.includes(field.key)) ? errors[errKey] : undefined;
    const rawLabel = labelOf(field.label) || field.label;
    const label = field.type === 'currency'
      ? chargeFieldLabel(rawLabel.replace(/\s*\((₹|\$|INR|Rs\.?)\)\s*$/i, ''))
      : rawLabel;
    const helper = labelOf(field.helperText) || field.helperText;
    const placeholder = labelOf(field.placeholder) || field.placeholder;

    if (field.type === 'boolean') {
      return (
        <FormControlLabel
          control={<Switch checked={!!values[field.key]} onChange={(e) => onChange(field.key, e.target.checked)} />}
          label={label}
        />
      );
    }

    if (isEntityField(field) && field.entitySource === 'restaurants') {
      return (
        <RestaurantSlugSelect
          label={label}
          value={String(values[field.key] ?? '')}
          onChange={(slug) => onChange(field.key, slug)}
          required={field.required}
          helperText={helper}
          error={!!fieldError}
          errorText={fieldError}
        />
      );
    }

    if (isZoneField(field)) {
      return (
        <ZoneMasterSelect
          label={label}
          value={String(values[field.key] ?? '')}
          onChange={(code) => onChange(field.key, code)}
          required={field.required}
          helperText={helper}
        />
      );
    }

    if (isMasterField(field, masterTypeKey)) {
      const refMasterType = getMasterTypeForField(field, masterTypeKey)!;
      if (field.multiple) {
        return (
          <SearchableMasterMultiSelect
            label={label}
            masterType={refMasterType}
            value={String(values[field.key] ?? '')}
            onChange={(codes) => onChange(field.key, codes)}
            required={field.required}
            helperText={helper}
            error={!!fieldError}
            errorText={fieldError}
          />
        );
      }
      return (
        <SearchableMasterSelect
          label={label}
          masterType={refMasterType}
          value={String(values[field.key] ?? '')}
          onChange={(code) => onChange(field.key, code)}
          required={field.required}
          helperText={helper}
          error={!!fieldError}
          errorText={fieldError}
        />
      );
    }

    if (field.type === 'select' && field.multiple) {
      const selected = field.key === 'daysOfWeek'
        ? parseCommaSeparatedDayValues(values[field.key])
        : String(values[field.key] ?? '')
          .split(',')
          .map((part) => part.trim())
          .filter(Boolean);
      return (
        <TextField
          select
          fullWidth
          size="small"
          label={label}
          value={selected}
          onChange={(e) => {
            const next = e.target.value;
            const codes = typeof next === 'string' ? next.split(',') : next;
            const normalized = field.key === 'daysOfWeek'
              ? formatCommaSeparatedDayValues(codes as string[])
              : (codes as string[]).join(',');
            onChange(field.key, normalized);
          }}
          helperText={fieldError || helper}
          required={field.required}
          error={!!fieldError}
          SelectProps={{
            multiple: true,
            renderValue: (chosen) => {
              const valuesSelected = chosen as string[];
              if (!valuesSelected.length) return translateUiText('— Select —', t);
              return valuesSelected
                .map((value) => field.options?.find((option) => option.value === value)?.label || value)
                .join(', ');
            },
          }}
        >
          {(field.options || []).map((o) => (
            <MenuItem key={o.value} value={o.value}>{labelOf(o.label) || o.label}</MenuItem>
          ))}
        </TextField>
      );
    }

    if (field.type === 'select') {
      return (
        <TextField
          select fullWidth size="small" label={label}
          value={values[field.key] ?? ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          helperText={fieldError || helper}
          required={field.required}
          error={!!fieldError}
        >
          <MenuItem value="">{translateUiText('— Select —', t)}</MenuItem>
          {(field.options || []).map((o) => (
            <MenuItem key={o.value} value={o.value}>{labelOf(o.label) || o.label}</MenuItem>
          ))}
        </TextField>
      );
    }

    if (isAssetUrlField(field)) {
      return (
        <FormFileUpload
          label={label}
          value={String(values[field.key] ?? '')}
          onChange={(url) => onChange(field.key, url)}
          folder={`masters/${masterTypeKey}/${field.key}`}
          helperText={helper}
          required={field.required}
          imageConstraints={imageConstraintsForField(field, masterTypeKey, values)}
        />
      );
    }

    if (field.type === 'time') {
      return (
        <TextField
          fullWidth
          size="small"
          label={label}
          type="time"
          value={String(values[field.key] ?? '')}
          onChange={(e) => onChange(field.key, e.target.value)}
          required={field.required}
          placeholder={placeholder || '06:00'}
          helperText={fieldError || helper || 'Use 24-hour format HH:mm (e.g. 06:00, 22:30)'}
          error={!!fieldError}
          inputProps={{ step: 60 }}
        />
      );
    }

    const isNumber = field.type === 'number' || field.type === 'percent' || field.type === 'currency';
    const isPassengerCapacity = isVehiclePassengerCapacityField(masterTypeKey, field.key);
    const isRideTagColor = isRideTagColorField(masterTypeKey, field.key);

    return (
      <TextField
        fullWidth size="small" label={label}
        type={(isPassengerCapacity || field.key === 'capacity') ? 'text' : isNumber ? 'number' : 'text'}
        value={values[field.key] ?? ''}
        onChange={(e) => {
          const raw = e.target.value;
          if (isRideTagColor) {
            const parsed = sanitizeRideTagColorInput(raw);
            if (parsed === null) return;
            onChange(field.key, parsed);
            return;
          }
          if (isPassengerCapacity || field.key === 'capacity') {
            const parsed = sanitizePassengerCapacityInput(raw);
            if (parsed === null) return;
            onChange(field.key, parsed);
            return;
          }
          if (isNumber) {
            if (/[-+,eE.]/.test(raw)) return;
            if (raw !== '' && Number.isNaN(Number(raw))) return;
            const next = raw === '' ? '' : Number(raw);
            if (next !== '' && !Number.isInteger(next)) return;
            if (next !== '' && field.min !== undefined && next < field.min) return;
            onChange(field.key, next);
            return;
          }
          onChange(field.key, raw);
        }}
        required={field.required || isPassengerCapacity || field.key === 'capacity'}
        placeholder={placeholder}
        helperText={
          fieldError
          || (isRideTagColor
            ? 'Use hex format — e.g. #f59e0b'
            : ((isPassengerCapacity || field.key === 'capacity')
              ? `Required — whole numbers from ${PASSENGER_CAPACITY_MIN} to ${PASSENGER_CAPACITY_MAX} seats`
              : helper))
        }
        error={!!fieldError}
        inputProps={{
          min: (isPassengerCapacity || field.key === 'capacity') ? PASSENGER_CAPACITY_MIN : field.min,
          max: (isPassengerCapacity || field.key === 'capacity') ? PASSENGER_CAPACITY_MAX : field.max,
          step: field.type === 'percent' ? 0.1 : (isPassengerCapacity || field.key === 'capacity') ? 1 : undefined,
          pattern: (isPassengerCapacity || field.key === 'capacity') ? '[0-9]*' : field.pattern,
          inputMode: (isPassengerCapacity || field.key === 'capacity') ? 'numeric' : undefined,
        }}
      />
    );
  };

  if (sections.length === 1 && sections[0][0] === 'Details') {
    return (
      <FormGrid>
        {fields.map((field) => (
          <Grid item xs={12} sm={field.type === 'boolean' ? 12 : 6} key={field.key}>
            {renderField(field)}
          </Grid>
        ))}
      </FormGrid>
    );
  }

  return (
    <>
      {sections.map(([section, sectionFields]) => (
        <FormSection key={section} title={section} divider>
          <FormGrid>
            {sectionFields.map((field) => (
              <Grid item xs={12} sm={field.type === 'boolean' ? 12 : 6} key={field.key}>
                {renderField(field)}
              </Grid>
            ))}
          </FormGrid>
        </FormSection>
      ))}
    </>
  );
}

export default function MasterFormDrawer({
  open,
  editing,
  schema,
  currentType,
  form,
  onChange,
  onClose,
  onSubmit,
  loading,
  parentMasterType,
  parentOptions = [],
  lockedSortOrder = null,
  existingRecords = [],
  editingId,
  saveError = null,
}: Props) {
  const [showErrors, setShowErrors] = useState(false);
  const [codeAuto, setCodeAuto] = useState(true);
  const supportsAutoCode = ON_DEMAND_AUTO_CODE_TYPES.has(currentType)
    || TRANSPORT_AUTO_CODE_TYPES.has(currentType);
  const autoServiceCodeField = supportsAutoCode
    ? schema?.metadataFields?.find((f) => f.key === 'serviceCode')
    : undefined;
  const letterOnlyName = TRANSPORT_LETTER_ONLY_NAME_TYPES.has(currentType);
  const validatedDescription = TRANSPORT_DESCRIPTION_VALIDATED_TYPES.has(currentType);
  const hideStatusToggle = TRANSPORT_HIDE_STATUS_TOGGLE_TYPES.has(currentType);
  const hideDescription = TRANSPORT_HIDE_DESCRIPTION_TYPES.has(currentType);
  const strictIntegerSortOrder = TRANSPORT_INTEGER_SORT_ORDER_TYPES.has(currentType);
  const uniqueSortOrder = TRANSPORT_UNIQUE_SORT_ORDER_TYPES.has(currentType)
    || TRANSPORT_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES.has(currentType);
  const parentScopedSortOrder = TRANSPORT_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES.has(currentType);
  const hasParent = !!(schema?.supportsHierarchy && parentMasterType);
  const parentOptional = hasParent && OPTIONAL_PARENT_TYPES.has(currentType);
  const parentRequired = hasParent && !parentOptional;
  const hasGeoFields = (schema?.geoFields?.length || 0) > 0;
  const metaFields = (schema?.metadataFields || []).filter((f) =>
    // sortOrder / status live on the record — avoid duplicate metadata fields
    f.key !== 'sortOrder' && f.key !== 'status'
    && !(hideDescription && f.key === 'description')
    && (!isTransportHubType(currentType) || (f.key !== 'centerLat' && f.key !== 'centerLng'))
    && !(autoServiceCodeField && f.key === 'serviceCode'),
  );
  const hasMeta = metaFields.length > 0;
  const isFoodMaster = currentType.startsWith('food_') || currentType.startsWith('restaurant_') || ['cuisine_types', 'addon_groups', 'kitchen_stations', 'preparation_times'].includes(currentType);
  const isOnDemandMaster = ON_DEMAND_AUTO_CODE_TYPES.has(currentType);
  const drawerWidth = isFoodMaster || isOnDemandMaster || currentType === 'service_categories' ? 640 : 520;

  useEffect(() => {
    if (open) {
      setShowErrors(false);
      setCodeAuto(!editing && supportsAutoCode);
    }
  }, [open, currentType, editing, supportsAutoCode]);

  const sortOrderAttributeCode = useMemo(() => {
    if (!parentScopedSortOrder) return undefined;
    const selected = parentOptions.find((p) => p._id === form.parentId);
    if (selected?.code) return selected.code;
    const legacyCode = form.metadata.attributeCode;
    return legacyCode != null ? String(legacyCode) : undefined;
  }, [parentScopedSortOrder, parentOptions, form.parentId, form.metadata.attributeCode]);

  const sortOrderScopeRecords = useMemo(
    () => filterRecordsForParentScopedSortOrder(currentType, existingRecords, {
      parentId: form.parentId,
      attributeCode: sortOrderAttributeCode,
    }),
    [currentType, existingRecords, form.parentId, sortOrderAttributeCode],
  );

  const errors = useMemo(() => {
    const base = validateMasterForm(form, schema, {
      hasParent: parentRequired,
      requiredSortOrder: !editing && lockedSortOrder != null ? lockedSortOrder : undefined,
      existingRecords: sortOrderScopeRecords,
      editingId,
      masterType: currentType,
      sortOrderParentId: form.parentId,
      sortOrderAttributeCode,
    });
    if (currentType === 'vehicle_types') {
      const capacityErr = validatePassengerCapacity(
        form.metadata.capacity,
        'Passenger Capacity',
        { required: true },
      );
      if (capacityErr) base['meta:capacity'] = capacityErr;
      else delete base['meta:capacity'];

      const serviceTypesErr = validateVehicleServiceTypes(form.metadata.serviceTypes);
      if (serviceTypesErr) base['meta:serviceTypes'] = serviceTypesErr;
      else delete base['meta:serviceTypes'];
    }
    return base;
  }, [form, schema, parentRequired, editing, lockedSortOrder, sortOrderScopeRecords, editingId, currentType, sortOrderAttributeCode]);

  const codeDuplicateError = useMemo(
    () => validateDuplicateMasterCode(form.code, existingRecords, editingId),
    [form.code, existingRecords, editingId],
  );

  const sortOrderDuplicateError = useMemo(
    () => (uniqueSortOrder
      ? validateDuplicateSortOrder(form.sortOrder, sortOrderScopeRecords, editingId)
      : undefined),
    [form.sortOrder, sortOrderScopeRecords, editingId, uniqueSortOrder],
  );

  const passengerCapacityError = useMemo(
    () => (currentType === 'vehicle_types'
      ? validatePassengerCapacity(form.metadata.capacity, 'Passenger Capacity', { required: true })
      : undefined),
    [currentType, form.metadata.capacity],
  );

  const vehicleServiceTypesError = useMemo(
    () => (currentType === 'vehicle_types'
      ? validateVehicleServiceTypes(form.metadata.serviceTypes)
      : undefined),
    [currentType, form.metadata.serviceTypes],
  );

  const rideTagColorError = useMemo(
    () => (currentType === 'ride_tags'
      ? validateRideTagColor(form.metadata.color, 'Tag Color')
      : undefined),
    [currentType, form.metadata.color],
  );

  const descriptionError = useMemo(
    () => (validatedDescription ? validateOptionalDescription(form.description) : undefined),
    [form.description, validatedDescription],
  );

  const setField = (patch: Partial<MasterFormState>) => onChange({ ...form, ...patch });
  const setMeta = (key: string, value: string | number | boolean) => {
    const metadata = { ...form.metadata, [key]: value };
    const patch: Partial<MasterFormState> = { metadata };
    const legacyKey = LEGACY_PARENT_META_KEY[currentType];
    if (legacyKey && key === legacyKey && typeof value === 'string' && value) {
      const parent = parentOptions.find((p) => p.code === value);
      if (parent) patch.parentId = parent._id;
    }
    onChange({ ...form, ...patch });
  };

  const parentLabel = currentType === 'service_categories'
    ? 'Parent category (primary)'
    : parentMasterType
      ? `Parent ${parentMasterType.replace(/_/g, ' ')}`
      : 'Parent master';

  const handleNameChange = (name: string) => {
    if (!editing && supportsAutoCode && codeAuto) {
      const code = slugifyMasterCode(name);
      setField({
        name,
        code,
        metadata: { ...form.metadata, serviceCode: code },
      });
    } else {
      setField({ name });
    }
  };

  const regenerateCodesFromName = () => {
    const code = slugifyMasterCode(form.name);
    setCodeAuto(true);
    setField({
      code,
      metadata: { ...form.metadata, serviceCode: code },
    });
  };

  const handleSubmit = () => {
    setShowErrors(true);
    const next = { ...form, metadata: { ...form.metadata } };
    if (!editing && supportsAutoCode && codeAuto) {
      const code = slugifyMasterCode(form.name) || form.code;
      next.code = code;
      next.metadata.serviceCode = code;
      if (next.code !== form.code || next.metadata.serviceCode !== form.metadata.serviceCode) {
        onChange(next);
      }
    }
    const nextErrors = validateMasterForm(next, schema, {
      hasParent: parentRequired,
      requiredSortOrder: !editing && lockedSortOrder != null ? lockedSortOrder : undefined,
      existingRecords,
      editingId,
      masterType: currentType,
    });
    if (!isMasterFormValid(nextErrors)) return;
    onSubmit();
  };

  return (
    <FormDrawer
      open={open}
      title={`${editing ? 'Edit' : 'Add'} ${schema?.label || currentType.replace(/_/g, ' ')}`}
      subtitle={schema?.description || 'Fill in the details below.'}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={editing ? 'Save' : 'Create'}
      loading={loading}
      disableSubmit={!!codeDuplicateError || !!sortOrderDuplicateError || !!descriptionError || !!passengerCapacityError || !!vehicleServiceTypesError || !!rideTagColorError}
      width={drawerWidth}
    >
      {showErrors && !isMasterFormValid(errors) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Please fix the highlighted fields before saving.
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={undefined}>
          {saveError}
        </Alert>
      )}

      <FormSection title="Basic info" divider>
        <FormGrid>
          <Grid item xs={12} sm={6}>
            {letterOnlyName ? (
              <PersonNameField
                required
                label="Display name"
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                helperText={
                  showErrors && errors.name
                    ? errors.name
                    : 'Letters only — no numbers or special characters'
                }
                error={showErrors && !!errors.name}
                autoFocus={!editing}
                inputProps={{ maxLength: 50 }}
              />
            ) : (
              <TextField
                required
                label="Display name"
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                helperText={showErrors && errors.name ? errors.name : undefined}
                error={showErrors && !!errors.name}
                autoFocus={!editing}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              label="Code"
              value={form.code}
              onChange={(e) => {
                setCodeAuto(false);
                const code = e.target.value.toUpperCase().replace(/\s+/g, '_');
                setField({
                  code,
                  metadata: autoServiceCodeField
                    ? { ...form.metadata, serviceCode: code }
                    : form.metadata,
                });
              }}
              disabled={editing || (supportsAutoCode && codeAuto)}
              helperText={
                (codeDuplicateError || (showErrors && errors.code))
                  ? (codeDuplicateError || errors.code)
                  : editing
                    ? 'Code cannot be changed'
                    : supportsAutoCode && codeAuto
                      ? 'Auto-filled from name — use the icon to regenerate or turn Auto off to type manually'
                      : `Unique ID (e.g. ${masterCodePlaceholder(currentType)})`
              }
              error={!!codeDuplicateError || (showErrors && !!errors.code)}
              InputProps={!editing && supportsAutoCode ? {
                endAdornment: (
                  <AutoCodeAdornment
                    auto={codeAuto}
                    disabled={!form.name.trim()}
                    onRegenerate={regenerateCodesFromName}
                    onAutoChange={(on) => {
                      setCodeAuto(on);
                      if (on) regenerateCodesFromName();
                    }}
                  />
                ),
              } : undefined}
            />
          </Grid>
          {autoServiceCodeField && (
            <Grid item xs={12} sm={6}>
              <TextField
                required
                label={autoServiceCodeField.label || 'Service Code'}
                value={String(form.metadata.serviceCode ?? (codeAuto ? form.code : '') ?? '')}
                onChange={(e) => {
                  setCodeAuto(false);
                  setMeta('serviceCode', e.target.value.toUpperCase().replace(/\s+/g, '_'));
                }}
                disabled={editing || codeAuto}
                helperText={
                  showErrors && errors['meta:serviceCode']
                    ? errors['meta:serviceCode']
                    : editing
                      ? 'Service code cannot be changed'
                      : codeAuto
                        ? 'Auto-filled from name — use the icon to regenerate or turn Auto off to type manually'
                        : autoServiceCodeField.helperText || 'Unique service identifier (e.g. LOCAL)'
                }
                error={showErrors && !!errors['meta:serviceCode']}
                InputProps={!editing ? {
                  endAdornment: (
                    <AutoCodeAdornment
                      auto={codeAuto}
                      disabled={!form.name.trim()}
                      onRegenerate={regenerateCodesFromName}
                      onAutoChange={(on) => {
                        setCodeAuto(on);
                        if (on) regenerateCodesFromName();
                      }}
                    />
                  ),
                } : undefined}
              />
            </Grid>
          )}
          {!hideDescription && (
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => {
                  const raw = e.target.value;
                  const description = validatedDescription
                    ? limitConsecutiveRepeatedCharacters(raw)
                    : raw;
                  setField({ description });
                }}
                placeholder="Optional admin notes"
                multiline
                minRows={2}
                error={!!descriptionError || (showErrors && !!errors.description)}
                helperText={
                  descriptionError
                  || (showErrors && errors.description)
                  || (validatedDescription
                    ? 'Avoid repeated characters or consecutive duplicate words'
                    : undefined)
                }
              />
            </Grid>
          )}
          <Grid item xs={12} sm={hideStatusToggle ? 12 : 6}>
            <TextField
              fullWidth
              type="number"
              label="Sort order"
              value={form.sortOrder}
              onChange={(e) => {
                if (lockedSortOrder != null && !editing) return;
                const raw = e.target.value;
                if (strictIntegerSortOrder && (/[-+,eE.]/.test(raw))) return;
                const next = raw === '' ? 0 : Number(raw);
                if (strictIntegerSortOrder && raw !== '' && (!Number.isInteger(next) || next < 1)) return;
                setField({ sortOrder: next || 0 });
              }}
              disabled={!editing && lockedSortOrder != null}
              helperText={
                (sortOrderDuplicateError || (showErrors && errors.sortOrder))
                  ? (sortOrderDuplicateError || errors.sortOrder)
                  : !editing && lockedSortOrder != null
                    ? `Automatically set to ${lockedSortOrder} — the next number in sequence`
                    : strictIntegerSortOrder
                      ? uniqueSortOrder
                        ? parentScopedSortOrder
                          ? 'Whole numbers only — must be unique within the selected ride attribute'
                          : 'Whole numbers only — must be unique across records'
                        : 'Whole numbers only — no decimals'
                      : 'Lower numbers appear first'
              }
              error={!!sortOrderDuplicateError || (showErrors && !!errors.sortOrder)}
              inputProps={{
                min: 1,
                max: 9999,
                step: 1,
                readOnly: !editing && lockedSortOrder != null ? true : undefined,
              }}
            />
          </Grid>
          {!hideStatusToggle && (
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={form.isActive !== false}
                    onChange={(e) => {
                      const isActive = e.target.checked;
                      setField({
                        isActive,
                        metadata: {
                          ...form.metadata,
                          ...(schema?.metadataFields?.some((f) => f.key === 'status')
                            ? { status: isActive ? 'active' : 'inactive' }
                            : {}),
                        },
                      });
                    }}
                  />
                )}
                label="Active"
              />
            </Grid>
          )}
        </FormGrid>
      </FormSection>

      {hasParent && (
        <FormSection
          title={currentType === 'service_categories' ? 'Category hierarchy' : 'Parent master'}
          description={
            parentOptional
              ? 'Leave empty for a primary category. Select a primary to create a sub-category.'
              : 'Search and select the parent record.'
          }
          divider
        >
          <ParentMasterSelect
            label={parentLabel}
            value={form.parentId}
            options={parentOptions}
            required={parentRequired}
            helperText={
              parentOptional
                ? 'Primary categories have no parent; sub-categories must select one'
                : undefined
            }
            error={showErrors && !!errors.parentId}
            errorText={showErrors ? errors.parentId : undefined}
            onChange={(parentId, selected) => {
              const legacyKey = LEGACY_PARENT_META_KEY[currentType];
              setField({
                parentId,
                country: selected?.code && parentMasterType === 'countries' ? selected.code : form.country,
                ...(legacyKey && selected?.code
                  ? { metadata: { ...form.metadata, [legacyKey]: selected.code } }
                  : {}),
              });
            }}
          />
          {!parentOptions.length && (
            <Typography variant="caption" color="warning.main" display="block" mt={0.75}>
              {parentOptional
                ? 'No primary categories yet — create one without a parent first.'
                : `No parent records found. Add ${parentMasterType?.replace(/_/g, ' ')} masters first.`}
            </Typography>
          )}
        </FormSection>
      )}

      {hasGeoFields && (
        <FormSection title="Location" divider>
          <FormGrid>
            {schema?.geoFields?.includes('country') && (
              <Grid item xs={12} sm={6}>
                <SearchableMasterSelect
                  label="Country"
                  masterType={GEO_FIELD_SOURCE.country || 'countries'}
                  value={form.country}
                  onChange={(code) => setField({ country: code })}
                />
              </Grid>
            )}
          </FormGrid>
          {isTransportHubType(currentType) && (
            <HubCoordinatePicker
              masterType={currentType}
              lat={Number(form.metadata.centerLat) || undefined}
              lng={Number(form.metadata.centerLng) || undefined}
              onChange={(lat, lng) => onChange({
                ...form,
                metadata: { ...form.metadata, centerLat: lat, centerLng: lng },
              })}
            />
          )}
        </FormSection>
      )}

      {hasMeta && (
        <FormSection title="Properties" divider>
          <MetadataFields
            fields={metaFields}
            values={form.metadata}
            onChange={setMeta}
            masterTypeKey={currentType}
            errors={errors}
            showErrors={showErrors}
            liveMetaKeys={currentType === 'vehicle_types'
              ? ['capacity', 'serviceTypes']
              : currentType === 'ride_tags'
                ? ['color']
                : currentType === 'service_availability_schedules'
                  ? ['startTime', 'endTime', 'daysOfWeek']
                  : []}
          />
        </FormSection>
      )}
    </FormDrawer>
  );
}
